import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Info, Loader2 } from 'lucide-react';
import backgroundProcessor from '../background/processor.ts';
import { usePrevious } from '../hooks/usePrevious';
import { supabase } from '../lib/supabaseClient';

interface FuturesTradingFormsProps {
  usdtBalance: number;
  availableBalance?: number;
  maxAllowedLeverage?: number;
  calculateLiquidationPrice: (
    side: 'long' | 'short',
    entryPrice: number,
    leverage: number,
    marginType: 'isolated' | 'cross',
    amount?: number,
    totalBalance?: number
  ) => number;
  onFuturesTrade: (
    side: 'long' | 'short', 
    amount: number, 
    leverage: number, 
    marginType: 'isolated' | 'cross',
    stopLoss?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    takeProfit?: { trigger_price: number; execution_type: 'market' | 'limit'; execution_price?: number },
    orderType?: 'market' | 'limit'
  ) => void;
  selectedPair?: string;
}

const FuturesTradingForms: React.FC<FuturesTradingFormsProps> = ({
  usdtBalance,
  availableBalance,
  maxAllowedLeverage = 100,
  calculateLiquidationPrice,
  onFuturesTrade,
  selectedPair = 'BTCUSDT'
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marginType, setMarginType] = useState<'isolated' | 'cross'>('isolated');
  const [leverage, setLeverage] = useState(Math.min(10, maxAllowedLeverage));
  const [orderType, setOrderType] = useState<'market' | 'limit'>('limit');
  const [longAmount, setLongAmount] = useState('');
  const [shortAmount, setShortAmount] = useState('');
  const [longPrice, setLongPrice] = useState('');
  const [shortPrice, setShortPrice] = useState('');
  const [longPercentage, setLongPercentage] = useState(0);
  const [shortPercentage, setShortPercentage] = useState(0);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [priceFlash, setPriceFlash] = useState<'none' | 'green' | 'red'>('none');
  const [isSubmittingLong, setIsSubmittingLong] = useState(false);
  const [isSubmittingShort, setIsSubmittingShort] = useState(false);
  
  // Get current price from market data for the selected pair
  const [currentPrice, setCurrentPrice] = useState(0);
  
  // Fetch current price for selected pair from Supabase
  useEffect(() => {
    const fetchCurrentPrice = async () => {
      try {
        console.log(`Fetching current price for ${selectedPair} from database...`);
        const { data, error } = await supabase
          .from('market_data')
          .select('price')
          .eq('symbol', selectedPair)
          .order('timestamp', { ascending: false })
          .limit(1)
          .single();
          
        if (!error && data && typeof data.price === 'number' && data.price > 0) {
          console.log(`Successfully fetched price for ${selectedPair}: ${data.price}`);
          setCurrentPrice(data.price);
        } else {
          console.warn(`No valid price found for ${selectedPair} in database. Error:`, error, 'Data:', data);
          // Don't set a fallback price here - let the component handle the zero price case
        }
      } catch (err) {
        console.error(`Error fetching current price for ${selectedPair}:`, err);
      }
    };
    
    fetchCurrentPrice();
    
    // Update price every 10 seconds to ensure fresh data
    const interval = setInterval(fetchCurrentPrice, 10000);
    
    return () => clearInterval(interval);
  }, [selectedPair]);
  
  // Track previous price for flash animation
  const previousPrice = usePrevious(currentPrice);

  // Track previous leverage for recalculating amounts
  const previousLeverage = usePrevious(leverage);

  // Calculate amount from percentage for long positions
  const calculateLongAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    // Calculate max position size based on available balance, leverage, and margin type
    const availableMargin = availableBalance !== undefined ? availableBalance : usdtBalance;
    const maxNotional = availableMargin * leverage;
    const maxAmount = maxNotional / currentPrice;
    
    // Calculate amount based on selected percentage
    const amount = (maxAmount * percentage) / 100;
    
    return amount.toFixed(6);
  }, [availableBalance, usdtBalance, leverage, currentPrice]);

  // Calculate amount from percentage for short positions
  const calculateShortAmountFromPercentage = useCallback((percentage: number) => {
    if (percentage <= 0) return '';
    
    // Calculate max position size based on available balance, leverage, and margin type
    const availableMargin = availableBalance !== undefined ? availableBalance : usdtBalance;
    const maxNotional = availableMargin * leverage;
    const maxAmount = maxNotional / currentPrice;
    
    // Calculate amount based on selected percentage
    const amount = (maxAmount * percentage) / 100;
    
    return amount.toFixed(6);
  }, [availableBalance, usdtBalance, leverage, currentPrice]);

  // Stop Loss / Take Profit states
  const [longStopLoss, setLongStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [longTakeProfit, setLongTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortStopLoss, setShortStopLoss] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });
  const [shortTakeProfit, setShortTakeProfit] = useState({ enabled: false, price: '', type: 'market' as 'market' | 'limit', limitPrice: '' });

  const percentageOptions = [25, 50, 75, 100];

  // Generate leverage options based on max allowed leverage
  const generateLeverageOptions = () => {
    const baseOptions = [1, 2, 3, 5, 10, 20, 25, 30, 50, 75, 100, 125, 150, 200, 250, 300, 400, 500, 750, 1000];
    return baseOptions.filter(option => option <= maxAllowedLeverage);
  };

  const leverageOptions = generateLeverageOptions();

  // Extract base currency symbol from selectedPair (e.g., "BTC" from "BTCUSDT", "SUI" from "SUIUSDT")
  const baseCurrencySymbol = selectedPair.replace('USDT', '');

  // Flash price when it changes
  useEffect(() => {
    if (previousPrice !== undefined && currentPrice !== previousPrice) {
      setPriceFlash(currentPrice > previousPrice ? 'green' : 'red');
      
      // Reset flash after animation completes
      const timer = setTimeout(() => {
        setPriceFlash('none');
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [currentPrice, previousPrice]);

  // Update price fields when currentPrice changes, but ONLY if orderType is 'market'
  // This is the key fix - only update the price fields automatically when in market order mode
  useEffect(() => {
    if (currentPrice && typeof currentPrice === 'number' && currentPrice > 0) {
      // Only update price fields if order type is 'market'
      if (orderType === 'market') {
        setLongPrice(currentPrice.toFixed(4));
        setShortPrice(currentPrice.toFixed(4));
      } else if (longPrice === '' && shortPrice === '') {
        // Initialize empty price fields with current price
        setLongPrice(currentPrice.toFixed(4));
        setShortPrice(currentPrice.toFixed(4));
      }
    }
  }, [currentPrice, orderType, longPrice, shortPrice]);

  // Update error message when error changes
  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      
      // Clear error after 5 seconds
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Recalculate amounts when leverage changes if percentage is selected
  useEffect(() => {
    if (previousLeverage !== undefined && leverage !== previousLeverage) {
      // Only recalculate if a percentage button is active
      if (longPercentage > 0) {
        const newAmount = calculateLongAmountFromPercentage(longPercentage);
        setLongAmount(newAmount);
      }
      if (shortPercentage > 0) {
        const newAmount = calculateShortAmountFromPercentage(shortPercentage);
        setShortAmount(newAmount);
      }
    }
  }, [leverage, previousLeverage, longPercentage, shortPercentage, calculateLongAmountFromPercentage, calculateShortAmountFromPercentage]);

  // Real-time recalculation when price or balance changes
  useEffect(() => {
    if (longPercentage > 0) {
      const newAmount = calculateLongAmountFromPercentage(longPercentage);
      setLongAmount(newAmount);
    }
  }, [currentPrice, usdtBalance, longPercentage, calculateLongAmountFromPercentage]);

  useEffect(() => {
    if (shortPercentage > 0) {
      const newAmount = calculateShortAmountFromPercentage(shortPercentage);
      setShortAmount(newAmount);
    }
  }, [currentPrice, usdtBalance, shortPercentage, calculateShortAmountFromPercentage]);

  // Calculate slider ranges for stop loss and take profit
  const getLongStopLossRange = () => {
    const priceToUse = parseFloat(longPrice) || currentPrice;
    return {
      min: Math.floor(priceToUse * 0.5), // 50% below current price
      max: Math.floor(priceToUse * 0.99), // 1% below current price
      step: 1
    };
  };

  const getLongTakeProfitRange = () => {
    const priceToUse = parseFloat(longPrice) || currentPrice;
    return {
      min: Math.ceil(priceToUse * 1.01), // 1% above current price
      max: Math.ceil(priceToUse * 2), // 100% above current price
      step: 1
    };
  };

  const getShortStopLossRange = () => {
    const priceToUse = parseFloat(shortPrice) || currentPrice;
    return {
      min: Math.ceil(priceToUse * 1.01), // 1% above current price
      max: Math.ceil(priceToUse * 2), // 100% above current price
      step: 1
    };
  };

  const getShortTakeProfitRange = () => {
    const priceToUse = parseFloat(shortPrice) || currentPrice;
    return {
      min: Math.floor(priceToUse * 0.5), // 50% below current price
      max: Math.floor(priceToUse * 0.99), // 1% below current price
      step: 1
    };
  };

  // Calculate estimated PnL for stop loss and take profit
  const calculateEstimatedPnl = (
    side: 'long' | 'short',
    entryPrice: number,
    triggerPrice: number,
    amount: number,
    leverage: number
  ): number => {
    if (side === 'long') {
      // For long positions: (triggerPrice - entryPrice) * amount
      return (triggerPrice - entryPrice) * amount;
    } else {
      // For short positions: (entryPrice - triggerPrice) * amount
      return (entryPrice - triggerPrice) * amount;
    }
  };

  const handleLong = async () => {
    // Prevent multiple submissions
    if (isSubmittingLong) return;
    
    setIsSubmittingLong(true);
    setErrorMessage(null);
    
    const amount = parseFloat(longAmount);
    if (!amount || amount <= 0) {
      setErrorMessage('Please enter a valid amount');
      setIsSubmittingLong(false);
      return;
    }

    const price = orderType === 'limit' ? parseFloat(longPrice) : undefined;
    if (orderType === 'limit' && (!price || price <= 0)) {
      setErrorMessage('Please enter a valid price for limit order');
      setIsSubmittingLong(false);
      return;
    }

    // Calculate required margin
    const notionalValue = amount * (price || currentPrice);
    const requiredMargin = notionalValue / leverage;
    
    const actualAvailableBalance = availableBalance !== undefined ? availableBalance : usdtBalance;
    if (requiredMargin > actualAvailableBalance) {
      setErrorMessage('Insufficient available balance');
      setIsSubmittingLong(false);
      return;
    }

    // Prepare stop loss and take profit values
    const stopLossValue = longStopLoss.enabled && longStopLoss.price ? parseFloat(longStopLoss.price) : undefined;
    const takeProfitValue = longTakeProfit.enabled && longTakeProfit.price ? parseFloat(longTakeProfit.price) : undefined;

    try {
      console.log(`FuturesTradingForms.handleLong: Calling onFuturesTrade with orderType=${orderType}`);
      
      // Call the onFuturesTrade prop instead of directly using openPosition
      const success = await onFuturesTrade(
        'long', 
        amount, 
        leverage, 
        marginType, 
        longStopLoss.enabled && longStopLoss.price ? {
          trigger_price: parseFloat(longStopLoss.price),
          execution_type: longStopLoss.type,
          execution_price: longStopLoss.type === 'limit' && longStopLoss.limitPrice ? parseFloat(longStopLoss.limitPrice) : undefined,
        } : undefined,
        longTakeProfit.enabled && longTakeProfit.price ? {
          trigger_price: parseFloat(longTakeProfit.price),
          execution_type: longTakeProfit.type,
          execution_price: longTakeProfit.type === 'limit' && longTakeProfit.limitPrice ? parseFloat(longTakeProfit.limitPrice) : undefined,
        } : undefined,
        orderType
      );

      if (success) {
        setSuccessMessage(orderType === 'market' 
          ? 'Long position opened successfully' 
          : 'Limit order placed successfully');
        
        // Reset form
        setLongAmount('');
        setLongPercentage(0);
        setLongStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
        setLongTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });

        // Immediately process the order to ensure quick execution
        await backgroundProcessor.processOrders();
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to open position');
    } finally {
      setIsSubmittingLong(false);
    }
  };

  const handleShort = async () => {
    // Prevent multiple submissions
    if (isSubmittingShort) return;
    
    setIsSubmittingShort(true);
    setErrorMessage(null);
    
    const amount = parseFloat(shortAmount);
    if (!amount || amount <= 0) {
      setErrorMessage('Please enter a valid amount');
      setIsSubmittingShort(false);
      return;
    }

    const price = orderType === 'limit' ? parseFloat(shortPrice) : undefined;
    if (orderType === 'limit' && (!price || price <= 0)) {
      setErrorMessage('Please enter a valid price for limit order');
      setIsSubmittingShort(false);
      return;
    }

    // Calculate required margin
    const notionalValue = amount * (price || currentPrice);
    const requiredMargin = notionalValue / leverage;
    
    const actualAvailableBalance = availableBalance !== undefined ? availableBalance : usdtBalance;
    if (requiredMargin > actualAvailableBalance) {
      setErrorMessage('Insufficient available balance');
      setIsSubmittingShort(false);
      return;
    }

    // Prepare stop loss and take profit values
    const stopLossValue = shortStopLoss.enabled && shortStopLoss.price ? parseFloat(shortStopLoss.price) : undefined;
    const takeProfitValue = shortTakeProfit.enabled && shortTakeProfit.price ? parseFloat(shortTakeProfit.price) : undefined;

    try {
      console.log(`FuturesTradingForms.handleShort: Calling onFuturesTrade with orderType=${orderType}`);
      
      // Call the onFuturesTrade prop instead of directly using openPosition
      const success = await onFuturesTrade(
        'short', 
        amount, 
        leverage, 
        marginType, 
        shortStopLoss.enabled && shortStopLoss.price ? {
          trigger_price: parseFloat(shortStopLoss.price),
          execution_type: shortStopLoss.type,
          execution_price: shortStopLoss.type === 'limit' && shortStopLoss.limitPrice ? parseFloat(shortStopLoss.limitPrice) : undefined,
        } : undefined,
        shortTakeProfit.enabled && shortTakeProfit.price ? {
          trigger_price: parseFloat(shortTakeProfit.price),
          execution_type: shortTakeProfit.type,
          execution_price: shortTakeProfit.type === 'limit' && shortTakeProfit.limitPrice ? parseFloat(shortTakeProfit.limitPrice) : undefined,
        } : undefined,
        orderType
      );

      if (success) {
        setSuccessMessage(orderType === 'market' 
          ? 'Short position opened successfully' 
          : 'Limit order placed successfully');
        
        // Reset form
        setShortAmount('');
        setShortPercentage(0);
        setShortStopLoss({ enabled: false, price: '', type: 'market', limitPrice: '' });
        setShortTakeProfit({ enabled: false, price: '', type: 'market', limitPrice: '' });

        // Immediately process the order to ensure quick execution
        await backgroundProcessor.processOrders();
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Failed to open position');
    } finally {
      setIsSubmittingShort(false);
    }
  };

  const handleLongPercentage = (percentage: number) => {
    setLongPercentage(percentage);
    const newAmount = calculateLongAmountFromPercentage(percentage);
    setLongAmount(newAmount);
  };

  const handleShortPercentage = (percentage: number) => {
    setShortPercentage(percentage);
    const newAmount = calculateShortAmountFromPercentage(percentage);
    setShortAmount(newAmount);
  };

  const calculateLongCost = () => {
    const amount = parseFloat(longAmount) || 0;
    const notionalValue = amount * currentPrice;
    return notionalValue / leverage;
  };

  const calculateShortCost = () => {
    const amount = parseFloat(shortAmount) || 0;
    const notionalValue = amount * currentPrice;
    return notionalValue / leverage;
  };

  // Calculate liquidation price for long position
  const longLiquidationPrice = calculateLiquidationPrice(
    'long', 
    currentPrice, 
    leverage, 
    marginType, 
    parseFloat(longAmount) || 0, 
    usdtBalance
  );
  
  // Calculate liquidation price for short position
  const shortLiquidationPrice = calculateLiquidationPrice(
    'short', 
    currentPrice, 
    leverage, 
    marginType, 
    parseFloat(shortAmount) || 0, 
    usdtBalance
  );

  // Handle slider changes for stop loss and take profit
  const handleLongStopLossSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLongStopLoss(prev => ({ ...prev, price: value }));
  };

  const handleLongTakeProfitSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLongTakeProfit(prev => ({ ...prev, price: value }));
  };

  const handleShortStopLossSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setShortStopLoss(prev => ({ ...prev, price: value }));
  };

  const handleShortTakeProfitSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setShortTakeProfit(prev => ({ ...prev, price: value }));
  };

  // Handle margin type change
  const handleMarginTypeChange = (type: 'isolated' | 'cross') => {
    setMarginType(type);
    
    // Recalculate position sizes if percentage buttons are active
    if (longPercentage > 0) {
      handleLongPercentage(longPercentage);
    }
    if (shortPercentage > 0) {
      handleShortPercentage(shortPercentage);
    }
  };

  // Handle leverage change
  const handleLeverageChange = (newLeverage: number) => {
    // Ensure leverage doesn't exceed max allowed
    const clampedLeverage = Math.min(newLeverage, maxAllowedLeverage);
    setLeverage(clampedLeverage);
    
    // Leverage changes will be handled by the useEffect that watches leverage changes
  };

  // Calculate estimated PnL for long stop loss
  const longStopLossPnl = longStopLoss.enabled && longStopLoss.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        parseFloat(longPrice) || currentPrice,
        parseFloat(longStopLoss.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for long take profit
  const longTakeProfitPnl = longTakeProfit.enabled && longTakeProfit.price && longAmount
    ? calculateEstimatedPnl(
        'long',
        parseFloat(longPrice) || currentPrice,
        parseFloat(longTakeProfit.price),
        parseFloat(longAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short stop loss
  const shortStopLossPnl = shortStopLoss.enabled && shortStopLoss.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        parseFloat(shortPrice) || currentPrice,
        parseFloat(shortStopLoss.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  // Calculate estimated PnL for short take profit
  const shortTakeProfitPnl = shortTakeProfit.enabled && shortTakeProfit.price && shortAmount
    ? calculateEstimatedPnl(
        'short',
        parseFloat(shortPrice) || currentPrice,
        parseFloat(shortTakeProfit.price),
        parseFloat(shortAmount),
        leverage
      )
    : null;

  return (
    <div className="bg-slate-800/30 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-slate-700/50 shadow-2xl">
      {/* Status Messages */}
      {errorMessage && (
        <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle size={20} className="text-red-400 flex-shrink-0" />
          <span className="text-red-400">{errorMessage}</span>
        </div>
      )}
      
      {successMessage && (
        <div className="mb-6 bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
          <Info size={20} className="text-green-400 flex-shrink-0" />
          <span className="text-green-400">{successMessage}</span>
        </div>
      )}

      {/* Margin Type and Leverage Controls */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div className="flex items-center gap-4 mb-4 md:mb-0">
          <div className="flex bg-slate-900/50 rounded-xl p-1 border border-slate-600/30">
            <button
              onClick={() => handleMarginTypeChange('isolated')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'isolated'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              {t('futures.isolated')}
            </button>
            <button
              onClick={() => handleMarginTypeChange('cross')}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                marginType === 'cross'
                  ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg shadow-blue-500/25'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
            >
              {t('futures.cross')}
            </button>
          </div>
          
          <div className="text-orange-400 font-bold text-2xl bg-gradient-to-r from-orange-400 to-amber-400 bg-clip-text text-transparent">
            {leverage}x
          </div>
        </div>

        {/* Leverage Slider */}
        <div className="flex items-center gap-4 flex-1">
          <span className="text-slate-400 text-sm whitespace-nowrap">{t('futures.leverage')}:</span>
          <div className="w-full flex items-center gap-2">
            <span className="text-xs text-slate-500">1x</span>
            <input
              type="range"
              min="1"
              max={maxAllowedLeverage}
              step="1"
              value={leverage}
              onChange={(e) => handleLeverageChange(parseInt(e.target.value))}
              className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
            />
            <span className="text-xs text-slate-500">{maxAllowedLeverage}x</span>
          </div>
        </div>
      </div>

      {/* Order Type Tabs */}
      <div className="flex gap-3 md:gap-6 mb-4 md:mb-6">
        <button
          onClick={() => setOrderType('market')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'market' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          {t('futures.market')}
        </button>
        <button
          onClick={() => setOrderType('limit')}
          className={`text-sm font-medium px-4 py-2 rounded-lg transition-all duration-300 ${
            orderType === 'limit' 
              ? 'text-cyan-400 bg-cyan-400/10 border border-cyan-400/30 shadow-lg shadow-cyan-400/20' 
              : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
          }`}
        >
          {t('futures.limit')}
        </button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 md:gap-6">
        {/* Long Form */}
        <div className="flex-1" translate="no">
          <div className="mb-4">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>{t('futures.available')}</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono" translate="no">
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USDT
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1">{t('futures.price')}(USDT)</label>
              <input
                type="text"
                value={orderType === 'market' ? currentPrice.toFixed(4) : longPrice}
                onChange={(e) => setLongPrice(e.target.value)}
                readOnly={orderType === 'market'}
                className={`w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono ${
                  priceFlash === 'green' ? 'animate-flash-green text-emerald-400' : 
                  priceFlash === 'red' ? 'animate-flash-red text-red-400' : ''
                }`}
                translate="no"
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1">{t('futures.amount')}({baseCurrencySymbol})</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="0.0004"
                  value={longAmount}
                  onChange={(e) => {
                    setLongAmount(e.target.value);
                    setLongPercentage(0);
                  }}
                  className="w-full bg-slate-900/50 text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium" translate="no">{baseCurrencySymbol}</span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleLongPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    longPercentage === percentage 
                      ? 'bg-gradient-to-r from-emerald-500 to-green-500 text-white shadow-lg shadow-emerald-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-4 md:pt-6 mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longStopLoss.enabled}
                  onChange={(e) => setLongStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">{t('futures.stopLoss')}</label>
              </div>
              {longStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('futures.price')}
                      value={longStopLoss.price}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {longStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${longStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  {/* Stop Loss Slider */}
                  <div className="px-1" translate="no">
                    <input
                      type="range"
                      min={getLongStopLossRange().min}
                      max={getLongStopLossRange().max}
                      step={getLongStopLossRange().step}
                      value={longStopLoss.price || getLongStopLossRange().min}
                      onChange={handleLongStopLossSliderChange}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>{getLongStopLossRange().min}</span>
                      <span>{getLongStopLossRange().max}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={longStopLoss.type}
                      onChange={(e) => setLongStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('futures.market')}</option>
                      <option value="limit">{t('futures.limit')}</option>
                    </select>
                    {longStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('futures.price')}
                        value={longStopLoss.limitPrice}
                        onChange={(e) => setLongStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div className="mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={longTakeProfit.enabled}
                  onChange={(e) => setLongTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">{t('futures.takeProfit')}</label>
              </div>
              {longTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('futures.price')}
                      value={longTakeProfit.price}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {longTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${longTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  {/* Take Profit Slider */}
                  <div className="px-1" translate="no">
                    <input
                      type="range"
                      min={getLongTakeProfitRange().min}
                      max={getLongTakeProfitRange().max}
                      step={getLongTakeProfitRange().step}
                      value={longTakeProfit.price || getLongTakeProfitRange().min}
                      onChange={handleLongTakeProfitSliderChange}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>{getLongTakeProfitRange().min}</span>
                      <span>{getLongTakeProfitRange().max}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={longTakeProfit.type}
                      onChange={(e) => setLongTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('futures.market')}</option>
                      <option value="limit">{t('futures.limit')}</option>
                    </select>
                    {longTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('futures.price')}
                        value={longTakeProfit.limitPrice}
                        onChange={(e) => setLongTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>{t('futures.requiredMargin')}</span>
                <span>{t('futures.liquidationPrice')}: <span className="text-red-400" translate="no">${longLiquidationPrice.toFixed(2)}</span></span>
              </div>
              <div className="text-slate-300 font-mono text-lg" translate="no">{calculateLongCost().toFixed(2)} USDT</div>
              {marginType === 'cross' && (
                <div className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  <span>{t('futures.crossMarginWarning')}</span>
                </div>
              )}
            </div>

            <button 
              onClick={handleLong}
              disabled={loading || isSubmittingLong}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-emerald-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              {isSubmittingLong ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>{t('futures.processing')}</span>
                </>
              ) : (
                t('futures.buyLong')
              )}
            </button>
          </div>
        </div>

        {/* Short Form */}
        <div className="flex-1" translate="no">
          <div className="mb-4">
            <div className="flex justify-between text-sm text-slate-400 mb-3">
              <span>{t('futures.available')}</span>
              <span className="flex items-center gap-2 text-emerald-400 font-mono" translate="no">
                {(availableBalance !== undefined ? availableBalance : usdtBalance).toFixed(4)} USDT
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Price Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1">{t('futures.price')}(USDT)</label>
              <input
                type="text"
                value={orderType === 'market' ? currentPrice.toFixed(4) : shortPrice}
                onChange={(e) => setShortPrice(e.target.value)}
                readOnly={orderType === 'market'}
                className={`w-full bg-slate-900/50 text-white px-4 py-3 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all font-mono ${
                  priceFlash === 'green' ? 'animate-flash-green text-emerald-400' : 
                  priceFlash === 'red' ? 'animate-flash-red text-red-400' : ''
                }`}
                translate="no"
              />
            </div>

            {/* Amount Input */}
            <div className="mb-3">
              <label className="block text-sm text-slate-400 mb-1">{t('futures.amount')}({baseCurrencySymbol})</label>
              <div className="relative">
                <input
                  type="text"
                  placeholder="0.0004"
                  value={shortAmount}
                  onChange={(e) => {
                    setShortAmount(e.target.value);
                    setShortPercentage(0);
                  }}
                  className="w-full bg-slate-900/50 text-white px-4 py-3 pr-16 rounded-xl border border-slate-600/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all hover:border-slate-500/50 font-mono"
                  translate="no"
                />
                <span className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm font-medium" translate="no">{baseCurrencySymbol}</span>
              </div>
            </div>

            {/* Percentage Buttons */}
            <div className="flex gap-1 md:gap-2 mb-3">
              {percentageOptions.map((percentage) => (
                <button
                  key={percentage}
                  onClick={() => handleShortPercentage(percentage)}
                  className={`flex-1 py-2 text-xs rounded-lg font-medium transition-all duration-300 ${
                    shortPercentage === percentage 
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg shadow-red-500/25 transform scale-105' 
                      : 'bg-slate-700/50 text-slate-400 hover:bg-slate-600/50 hover:text-white border border-slate-600/30'
                  }`}
                >
                  {percentage}%
                </button>
              ))}
            </div>

            {/* Stop Loss */}
            <div className="border-t border-slate-700/50 pt-4 md:pt-6 mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortStopLoss.enabled}
                  onChange={(e) => setShortStopLoss(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-red-500 focus:ring-red-500/50"
                />
                <label className="text-sm text-slate-400">{t('futures.stopLoss')}</label>
              </div>
              {shortStopLoss.enabled && (
                <div className="space-y-3 bg-red-500/5 rounded-xl p-4 border border-red-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('futures.price')}
                      value={shortStopLoss.price}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-red-500/30 focus:outline-none focus:ring-1 focus:ring-red-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {shortStopLossPnl !== null && (
                      <div className="ml-2 text-red-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${shortStopLossPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  {/* Stop Loss Slider */}
                  <div className="px-1" translate="no">
                    <input
                      type="range"
                      min={getShortStopLossRange().min}
                      max={getShortStopLossRange().max}
                      step={getShortStopLossRange().step}
                      value={shortStopLoss.price || getShortStopLossRange().min}
                      onChange={handleShortStopLossSliderChange}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>{getShortStopLossRange().min}</span>
                      <span>{getShortStopLossRange().max}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={shortStopLoss.type}
                      onChange={(e) => setShortStopLoss(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-red-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('futures.market')}</option>
                      <option value="limit">{t('futures.limit')}</option>
                    </select>
                    {shortStopLoss.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('futures.price')}
                        value={shortStopLoss.limitPrice}
                        onChange={(e) => setShortStopLoss(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-red-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Take Profit */}
            <div className="mb-3">
              <div className="flex items-center gap-3 mb-3">
                <input
                  type="checkbox"
                  checked={shortTakeProfit.enabled}
                  onChange={(e) => setShortTakeProfit(prev => ({ ...prev, enabled: e.target.checked }))}
                  className="rounded bg-slate-800 border-slate-600 text-emerald-500 focus:ring-emerald-500/50"
                />
                <label className="text-sm text-slate-400">{t('futures.takeProfit')}</label>
              </div>
              {shortTakeProfit.enabled && (
                <div className="space-y-3 bg-emerald-500/5 rounded-xl p-4 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <input
                      type="text"
                      placeholder={t('futures.price')}
                      value={shortTakeProfit.price}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, price: e.target.value }))}
                      className="w-full bg-slate-900/50 text-white px-3 py-2 rounded-lg border border-emerald-500/30 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-sm font-mono"
                      translate="no"
                    />
                    {shortTakeProfitPnl !== null && (
                      <div className="ml-2 text-emerald-400 text-sm font-medium whitespace-nowrap" translate="no">
                        ${shortTakeProfitPnl.toFixed(2)}
                      </div>
                    )}
                  </div>
                  
                  {/* Take Profit Slider */}
                  <div className="px-1" translate="no">
                    <input
                      type="range"
                      min={getShortTakeProfitRange().min}
                      max={getShortTakeProfitRange().max}
                      step={getShortTakeProfitRange().step}
                      value={shortTakeProfit.price || getShortTakeProfitRange().min}
                      onChange={handleShortTakeProfitSliderChange}
                      className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer"
                    />
                    <div className="flex justify-between text-xs text-slate-500 mt-1">
                      <span>{getShortTakeProfitRange().min}</span>
                      <span>{getShortTakeProfitRange().max}</span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <select
                      value={shortTakeProfit.type}
                      onChange={(e) => setShortTakeProfit(prev => ({ ...prev, type: e.target.value as 'market' | 'limit' }))}
                      className="flex-1 bg-slate-900/50 text-white px-2 pr-10 py-1 rounded-lg border border-emerald-500/30 text-sm custom-select"
                    >
                      <option value="market">{t('futures.market')}</option>
                      <option value="limit">{t('futures.limit')}</option>
                    </select>
                    {shortTakeProfit.type === 'limit' && (
                      <input
                        type="text"
                        placeholder={t('futures.price')}
                        value={shortTakeProfit.limitPrice}
                        onChange={(e) => setShortTakeProfit(prev => ({ ...prev, limitPrice: e.target.value }))}
                        className="flex-1 bg-slate-900/50 text-white px-2 py-1 rounded-lg border border-emerald-500/30 text-sm font-mono"
                        translate="no"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mb-3">
              <div className="flex justify-between text-sm text-slate-400 mb-2">
                <span>{t('futures.requiredMargin')}</span>
                <span>{t('futures.liquidationPrice')}: <span className="text-red-400" translate="no">${shortLiquidationPrice.toFixed(2)}</span></span>
              </div>
              <div className="text-slate-300 font-mono text-lg" translate="no">{calculateShortCost().toFixed(2)} USDT</div>
              {marginType === 'cross' && (
                <div className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  <span>{t('futures.crossMarginWarning')}</span>
                </div>
              )}
            </div>

            <button 
              onClick={handleShort}
              disabled={loading || isSubmittingShort}
              className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 disabled:from-slate-700 disabled:to-slate-800 text-white py-4 rounded-xl font-semibold transition-all duration-300 shadow-lg shadow-red-500/25 transform hover:scale-105 flex items-center justify-center gap-2"
            >
              {isSubmittingShort ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>{t('futures.processing')}</span>
                </>
              ) : (
                t('futures.sellShort')
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FuturesTradingForms;